import { formatDisplayUnits } from "../helpers/amountDisplay";
import React from "react";
import { ethers } from "ethers";
import type { PrivateFeePolicy, FeeAction } from "../hooks/usePrivateFeePolicy";
import { WalletFeeSummary } from "./WalletFeeSummary";

export function privateFeeAmounts(
  policy: PrivateFeePolicy,
  action: FeeAction,
  amount: string,
) {
  const gross = ethers.parseUnits(amount || "0", policy.decimals ?? 18),
    rates = policy.rates[action];
  const redemption =
    action === "withdraw" &&
    gross > 0n &&
    BigInt(policy.feeCredit || "0") >= gross;
  const protocol = redemption
    ? 0n
    : (gross * BigInt(rates.protocolBps)) / 10000n;
  const partner = redemption ? 0n : (gross * BigInt(rates.partnerBps)) / 10000n;
  const gas = action === "deposit" ? 0n : BigInt(policy.gasReimbursementAmount);
  const total = protocol + partner + gas;
  return { protocol, partner, gas, redemption, total, net: gross - total };
}
export function PrivateFeeSummary({
  policy,
  action,
  amount,
}: {
  policy: PrivateFeePolicy | null;
  action: FeeAction;
  amount: string;
}) {
  if (!policy)
    return (
      <p role="status">
        {!amount || Number(amount) <= 0
          ? "Enter an amount to estimate fees."
          : "Checking transaction fees…"}
      </p>
    );
  let fees;
  try {
    fees = privateFeeAmounts(policy, action, amount);
  } catch {
    return null;
  }
  const rates = policy.rates[action];
  const symbol = policy.symbol ?? "ETH",
    format = (n: bigint, round: "down" | "up" = "up") =>
      formatDisplayUnits(n, policy.decimals ?? 18, round);
  return (
    <WalletFeeSummary
      rows={[
        {
          label: `Protocol fee (${fees.redemption ? 0 : rates.protocolBps / 100}%)`,
          value: `${format(fees.protocol)} ${symbol}`,
        },
        {
          label: `Partner fee (${fees.redemption ? 0 : rates.partnerBps / 100}%)`,
          value: `${format(fees.partner)} ${symbol}`,
        },
        ...(action !== "deposit"
          ? [
              {
                label: "Estimated network fee",
                value: `${format(fees.gas)} ${symbol}`,
              },
            ]
          : []),
        { label: "Total fee", value: `${format(fees.total)} ${symbol}` },
      ]}
      received={`${format(fees.net > 0n ? fees.net : 0n, "down")} ${symbol}`}
    >
      {fees.redemption && (
        <div>Fee balance withdrawal · No protocol or partner fee</div>
      )}
      {action === "deposit" && (
        <div>Network gas is paid separately by your connected wallet.</div>
      )}
    </WalletFeeSummary>
  );
}
